import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, effect, inject, Input, input, ViewEncapsulation } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { DateTime } from '@common/data-models/complex-types/date-time';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { type Moment } from 'moment-timezone';

@Component({
    selector: 'st-date-range-pager',
    templateUrl: './date-range-pager.html',
    styleUrls: ['./date-range-pager.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-date-range-pager',
    },
})
export class DateRangePagerComponent {
    private store = inject(Store);

    @Input()
    public isEnabled: boolean = true;

    private _datePickerEnabled: boolean = false;
    @Input()
    get datePickerEnabled(): boolean {
        return this._datePickerEnabled;
    }
    set datePickerEnabled(value: BooleanInput) {
        this._datePickerEnabled = coerceBooleanProperty(value);
    }

    period = input<{ firstDate: DateTime; lastDate: DateTime }>(null);
    dayCount = input<number>(7);

    // need a way to pass the current period
    public label: string = '';
    public firstDate: DateTime = new DateTime();
    public lastDate: DateTime = new DateTime();

    public get firstDateValue(): Moment {
        return this.firstDate ? this.firstDate.moment : null;
    }
    public set firstDateValue(v: Moment) {
        this.firstDate.set(v.toDate());
        this.lastDate.set(v.toDate());
        this.updateLabel();
        this.store.dispatch(ViewStateActions.adjustDateRange({ firstDate: this.firstDate.date, lastDate: this.lastDate.date }));
    }

    constructor(public appService: AppService) {
        effect(
            () => {
                const period = this.period();
                if (period) {
                    this.firstDate.set(period.firstDate.date);
                    this.lastDate.set(period.lastDate.date);
                    this.updateLabel();
                }
            },
            { allowSignalWrites: true }
        );
    }

    public prev() {
        if (!this.isEnabled) return;
        // recalculate date range, update label
        // we just need to substract 7 days from the first and the last
        const firstDate = this.firstDate?.moment?.subtract(this.dayCount(), 'days').toDate();
        const lastDate = this.lastDate?.moment?.subtract(this.dayCount(), 'days').toDate();

        if (!firstDate || !lastDate) return;
        this.store.dispatch(ViewStateActions.adjustDateRange({ firstDate, lastDate }));
    }

    public next() {
        if (!this.isEnabled) return;
        // recalculate date range, update label
        // we just need to add 7 days to the first and the last
        const firstDate = this.firstDate?.moment?.add(this.dayCount(), 'days').toDate();
        const lastDate = this.lastDate?.moment?.add(this.dayCount(), 'days').toDate();

        if (!firstDate || !lastDate) return;
        this.store.dispatch(ViewStateActions.adjustDateRange({ firstDate, lastDate }));
    }

    public updateLabel() {
        if (this.dayCount() === 1) {
            this.label = `${this.firstDate.moment.format('MMM')} ${this.firstDate.moment.date()}, ${this.firstDate.moment.format('Y')}`;
        } else {
            // based on firstDate and lastDate, update the label.
            // take into account months (if they are different, years, etc. )

            // 1. if month is the same and year is the same
            if (this.firstDate.moment.month() === this.lastDate.moment.month() && this.firstDate.moment.year() === this.lastDate.moment.year()) {
                this.label = `${this.firstDate.moment.format('MMM')} ${this.firstDate.moment.date()} - ${this.lastDate.moment.date()} , ${this.firstDate.moment.format('Y')}`;
            }
            // 2. if year is the same but month is different
            if (this.firstDate.moment.year() === this.lastDate.moment.year() && this.firstDate.moment.month() !== this.lastDate.moment.month()) {
                this.label = `${this.firstDate.moment.format('MMM')} ${this.firstDate.moment.date()} - ${this.lastDate.moment.format(
                    'MMM'
                )} ${this.lastDate.moment.date()} , ${this.firstDate.moment.format('Y')}`;
            }

            // 3. if both months and years are different
            if (this.firstDate.moment.year() !== this.lastDate.moment.year() && this.firstDate.moment.month() !== this.lastDate.moment.month()) {
                this.label = `${this.firstDate.moment.format('MMM')} ${this.firstDate.moment.date()}, ${this.firstDate.moment.format('Y')} - ${this.lastDate.moment.format(
                    'MMM'
                )} ${this.lastDate.moment.date()}, ${this.lastDate.moment.format('Y')}`;
            }
        }
    }

    public toggleDatePickerPopOver(picker: MatDatepicker<Moment>) {
        if (!this.isEnabled) return;
        if (this.datePickerEnabled) {
            picker.open();
        }
    }
}
